<template>
  <div class="document-preview">
    <!-- PDF Viewer -->
    <div class="pdf-viewer">
      <div class="pdf-container" ref="pdfContainer">
        <!-- PDF.js Viewer (Full Features) -->
        <iframe v-if="pdfSource && !loading" ref="pdfFrame" class="pdf-iframe" :src="advancedViewerUrl" @load="onAdvancedViewerLoaded" @error="onAdvancedViewerError"></iframe>

        <!-- Loading State -->
        <div v-else-if="loading" class="loading-state">
          <q-spinner size="50px" />
          <p>Loading document...</p>
        </div>

        <!-- Error State -->
        <div v-else class="error-state">
          <q-icon name="error" size="50px" color="negative" />
          <p>Failed to load document</p>
          <q-btn flat @click="downloadFile">Download file instead</q-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getDocumentType } from '@/utils/fileUpload';
import { getDocumentFileUrl, getDocumentViewingUrl } from '@/utils/documentProcessingUtils';

export default {
  name: 'DocumentPreview',
  props: {
    content: {
      type: Object,
      required: true
    },
    showToolbar: {
      type: Boolean,
      default: true
    },
    enableSearch: {
      type: Boolean,
      default: true
    },
    enableSidebar: {
      type: Boolean,
      default: true
    },
    documentProcessingStatuses: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      pdfSource: null,
      localPdfUrl: null, // Local blob URL for CORS-free viewing
      loading: true,
      error: false
    };
  },
  computed: {
    documentType() {
      return getDocumentType(this.content);
    },
    fileName() {
      return this.content.filename || this.content.name || 'Document';
    },
    fileSize() {
      if (this.content.size) {
        const bytes = this.content.size;
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
        return Math.round(bytes / 1048576) + ' MB';
      }
      return '';
    },
    fileUrl() {
      // Use the viewing URL for PDF viewer (prioritizes PDF version for better browser compatibility)
      return getDocumentViewingUrl(this.content, this.documentProcessingStatuses);
    },
    downloadUrl() {
      // Use the download URL for file downloads (always original document)
      return getDocumentFileUrl(this.content, this.documentProcessingStatuses);
    },
    advancedViewerUrl() {
      // Always try to show PDF viewer if we have a PDF source
      if (!this.localPdfUrl) return '';

      // Build PDF.js viewer URL with local blob URL to avoid CORS issues
      const baseUrl = '/pdfjs/web/viewer.html';
      const params = new URLSearchParams();

      // Use the local blob URL instead of the remote S3 URL
      params.append('file', this.localPdfUrl);

      // Advanced features from props
      if (!this.showToolbar) {
        params.append('toolbar', '0');
      }
      if (!this.enableSidebar) {
        params.append('sidebar', '0');
      }

      return `${baseUrl}?${params.toString()}`;
    },
    pageCount() {
      return this.content.extra_attributes?.page_count || null;
    }
  },
  mounted() {
    this.loadDocument();
  },

  beforeDestroy() {
    // Clean up blob URL to prevent memory leaks
    if (this.localPdfUrl) {
      URL.revokeObjectURL(this.localPdfUrl);
      this.localPdfUrl = null;
    }
  },

  watch: {
    fileUrl() {
      // Reload PDF if URL changes
      this.loadDocument();
    }
  },

  methods: {
    async loadDocument() {
      // Always try to load documents as PDF (backend converts all documents to PDF)
      if (!this.fileUrl) {
        this.loading = false;
        this.error = true;
        return;
      }

      try {
        this.loading = true;
        this.error = false;

        // Fetch document from remote URL and create local blob URL to avoid CORS issues
        const response = await fetch(this.fileUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch document: ${response.status}`);
        }

        const blob = await response.blob();

        // Clean up previous blob URL if it exists
        if (this.localPdfUrl) {
          URL.revokeObjectURL(this.localPdfUrl);
        }

        // Create local blob URL
        this.localPdfUrl = URL.createObjectURL(blob);
        this.pdfSource = this.localPdfUrl;
      } catch (error) {
        console.error('Error loading document:', error);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },

    onAdvancedViewerLoaded() {
      this.loading = false;
      this.error = false;
      this.$emit('viewer-loaded');
    },

    onAdvancedViewerError(error) {
      console.error('❌ Advanced PDF.js viewer error:', error);
      console.error('❌ Advanced viewer URL that failed:', this.advancedViewerUrl);
      this.error = true;
      this.loading = false;
    },

    downloadFile() {
      if (this.downloadUrl) {
        const link = document.createElement('a');
        link.href = this.downloadUrl;
        link.download = this.fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },

    openInNewTab() {
      if (this.downloadUrl) {
        window.open(this.downloadUrl, '_blank');
      }
    },

    // Advanced PDF.js control methods (for external use if needed)
    async searchText(query) {
      if (this.$refs.pdfFrame?.contentWindow) {
        const viewer = this.$refs.pdfFrame.contentWindow;
        if (viewer.PDFViewerApplication && viewer.PDFViewerApplication.findController) {
          viewer.PDFViewerApplication.findController.executeCommand('find', {
            query,
            caseSensitive: false,
            entireWord: false,
            highlightAll: true
          });
        }
      }
    },

    setDarkMode(enabled) {
      if (this.$refs.pdfFrame?.contentWindow) {
        const viewer = this.$refs.pdfFrame.contentWindow;
        if (viewer.PDFViewerApplication && viewer.PDFViewerApplication.pdfViewer) {
          viewer.PDFViewerApplication.pdfViewer.pageColors = enabled
            ? {
                background: 'rgb(42, 46, 51)',
                foreground: 'rgb(249, 249, 250)'
              }
            : null;
        }
      }
    },

    zoomTo(scale) {
      if (this.$refs.pdfFrame?.contentWindow) {
        const viewer = this.$refs.pdfFrame.contentWindow;
        if (viewer.PDFViewerApplication && viewer.PDFViewerApplication.pdfViewer) {
          viewer.PDFViewerApplication.pdfViewer.currentScale = scale;
        }
      }
    },

    goToPage(pageNumber) {
      if (this.$refs.pdfFrame?.contentWindow) {
        const viewer = this.$refs.pdfFrame.contentWindow;
        if (viewer.PDFViewerApplication && viewer.PDFViewerApplication.pdfViewer) {
          viewer.PDFViewerApplication.pdfViewer.currentPageNumber = pageNumber;
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.document-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pdf-viewer {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pdf-container {
  flex: 1;
  width: 100%;
  height: 100%;
  position: relative;
  background-color: #f5f5f5;
}

/* PDF.js iframe styling */
.pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
  background-color: white;
  position: absolute;
  top: 0;
  left: 0;
}

.loading-state,
.error-state,
.processing-state,
.failed-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  text-align: center;
  color: var(--secondary-text-color);
  padding: 40px;
  height: 100%;
}

.processing-info,
.failed-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.processing-info h4,
.failed-info h4 {
  @include mona-sans-font($size: 18px, $bolded: true, $color: var(--main-text-color));
  margin: 0;
}

.doc-name {
  @include inter-font($size: 16px, $bolded: true, $color: var(--main-text-color));
  margin: 0;
}

.doc-details {
  @include inter-font($size: 14px, $color: var(--secondary-text-color));
  margin: 0;
}
</style>
